import React, { useEffect, useMemo, useState } from "react";
import { getJSON } from "../lib/api";

/**
 * GAStreaming — Patch Note Hub
 * - Loads the game catalog from GET /api/game-patches
 * - Loads a game's latest items from GET /api/game-patches/:key
 * - Uses official publisher pages and Steam news feeds
 */

const CATEGORY_ORDER = [
  "All",
  "Sports",
  "Battle Royale",
  "Shooters",
  "Competitive",
  "Action & Co-op",
  "Other",
];

function fmtDate(value) {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "";
  }
}

function searchText(game) {
  return [game.key, game.name, game.category, game.description, ...(game.aliases || [])]
    .join(" ")
    .toLowerCase();
}

export default function Patches() {
  const [feeds, setFeeds] = useState({});
  const [itemsByKey, setItemsByKey] = useState({});
  const [openByKey, setOpenByKey] = useState({});
  const [loadingByKey, setLoadingByKey] = useState({});
  const [errorByKey, setErrorByKey] = useState({});
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [catalogLoading, setCatalogLoading] = useState(true);

  useEffect(() => {
    let active = true;

    (async () => {
      setCatalogLoading(true);
      const response = await getJSON("/game-patches");
      if (!active) return;

      if (response.ok && response.data?.ok && response.data?.feeds) {
        setFeeds(response.data.feeds || {});
      } else {
        setFeeds({});
      }
      setCatalogLoading(false);
    })();

    return () => {
      active = false;
    };
  }, []);

  const allGames = useMemo(() => {
    return Object.entries(feeds || {})
      .map(([key, value]) => ({
        key,
        name: value?.name || key,
        category: value?.category || "Other",
        description: value?.description || "Official game updates",
        link: value?.link || "",
        steamAppId: value?.steamAppId || null,
        order: Number.isFinite(Number(value?.order)) ? Number(value.order) : 999,
        aliases: Array.isArray(value?.aliases) ? value.aliases : [],
      }))
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
  }, [feeds]);

  const categories = useMemo(() => {
    const available = new Set(allGames.map((game) => game.category));
    return CATEGORY_ORDER.filter((item) => item === "All" || available.has(item));
  }, [allGames]);

  const visibleGames = useMemo(() => {
    const normalizedQuery = String(query || "").trim().toLowerCase();

    return allGames.filter((game) => {
      const matchesCategory = category === "All" || game.category === category;
      const matchesSearch = !normalizedQuery || searchText(game).includes(normalizedQuery);
      return matchesCategory && matchesSearch;
    });
  }, [allGames, category, query]);

  async function loadLatest(key) {
    setLoadingByKey((current) => ({ ...current, [key]: true }));
    setErrorByKey((current) => ({ ...current, [key]: "" }));

    const response = await getJSON(`/game-patches/${encodeURIComponent(key)}`);

    if (response.ok && response.data?.ok) {
      const items = Array.isArray(response.data.items) ? response.data.items : [];
      setItemsByKey((current) => ({ ...current, [key]: items }));
      setOpenByKey((current) => ({ ...current, [key]: true }));

      if (items.length === 0) {
        setErrorByKey((current) => ({
          ...current,
          [key]: "No notes were found automatically. Use Open official for the publisher's latest page.",
        }));
      } else if (response.data?.source === "fallback") {
        setErrorByKey((current) => ({
          ...current,
          [key]: "This publisher blocks automatic previews, so the official patch-note page is shown instead.",
        }));
      }
    } else {
      setItemsByKey((current) => ({ ...current, [key]: [] }));
      setErrorByKey((current) => ({
        ...current,
        [key]: "The notes could not be loaded right now. Open the official source or try again shortly.",
      }));
    }

    setLoadingByKey((current) => ({ ...current, [key]: false }));
  }

  function toggle(key) {
    setOpenByKey((current) => ({ ...current, [key]: !current[key] }));
  }

  return (
    <div className="ga-patch-page">
      <section className="ga-patch-hero">
        <div className="ga-patch-hero__copy">
          <div className="ga-patch-eyebrow">GAStreaming Game Updates</div>
          <h1 className="ga-patch-title">Patch Note Hub</h1>
          <p className="ga-patch-subtitle">
            Check official patch notes, title updates, hotfixes, and balance changes for the games
            GAStreaming players watch and play.
          </p>

          <div className="ga-patch-stats" aria-label="Patch Note Hub coverage">
            <span>{allGames.length || 0} games tracked</span>
            <span>Official publisher sources</span>
            <span>Steam feeds where available</span>
          </div>
        </div>

        <label className="ga-patch-search">
          <span className="ga-patch-search__label">Find a game</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search Madden, Warzone, Fortnite, 2K…"
            className="ga-patch-input"
          />
        </label>
      </section>

      <section className="ga-patch-toolbar" aria-label="Patch-note categories">
        <div className="ga-patch-filters">
          {categories.map((item) => (
            <button
              key={item}
              type="button"
              className={`ga-patch-filter${category === item ? " is-active" : ""}`}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="ga-patch-result-count">
          Showing {visibleGames.length} of {allGames.length} games
        </div>
      </section>

      {catalogLoading ? (
        <div className="ga-patch-state">Loading the Patch Note Hub…</div>
      ) : visibleGames.length === 0 ? (
        <div className="ga-patch-state">
          No games match that search. Clear the search or choose another category.
        </div>
      ) : (
        <div className="ga-patch-grid">
          {visibleGames.map((game) => {
            const open = Boolean(openByKey[game.key]);
            const loading = Boolean(loadingByKey[game.key]);
            const error = errorByKey[game.key] || "";
            const items = itemsByKey[game.key] || [];
            const sourceLabel = game.steamAppId ? "Steam news feed" : "Official publisher source";

            return (
              <article
                key={game.key}
                className={`ga-patch-card${open ? " ga-patch-card--open" : ""}`}
              >
                <div className="ga-patch-card__top">
                  <div className="ga-patch-card__heading">
                    <div className="ga-patch-card__badges">
                      <span className="ga-patch-category">{game.category}</span>
                      <span className="ga-patch-source">{sourceLabel}</span>
                    </div>
                    <h2 className="ga-patch-card__title">{game.name}</h2>
                    <p className="ga-patch-card__description">{game.description}</p>
                  </div>

                  <div className="ga-patch-card__actions">
                    <a
                      href={game.link || "#"}
                      target="_blank"
                      rel="noreferrer"
                      className="ga-patch-btn ga-patch-btn--ghost"
                      onClick={(event) => {
                        if (!game.link) event.preventDefault();
                      }}
                      title={`Open ${game.name} official updates`}
                    >
                      Open official
                    </a>

                    <button
                      type="button"
                      className="ga-patch-btn"
                      onClick={() => loadLatest(game.key)}
                      disabled={loading}
                    >
                      {loading ? "Checking…" : "Check notes"}
                    </button>

                    <button
                      type="button"
                      className="ga-patch-btn ga-patch-btn--ghost"
                      onClick={() => toggle(game.key)}
                    >
                      {open ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                {error ? <div className="ga-patch-note ga-patch-note--warn">{error}</div> : null}

                {!open ? (
                  <div className="ga-patch-card__hint">
                    {items.length
                      ? `${items.length} updates loaded. Select Show to reopen them.`
                      : "Select Check notes to pull the latest available updates."}
                  </div>
                ) : (
                  <div className="ga-patch-list">
                    {items.length === 0 ? (
                      <div className="ga-patch-empty">No updates loaded yet.</div>
                    ) : (
                      items.slice(0, 12).map((item) => {
                        const dateText = fmtDate(item.date);
                        const metaParts = [dateText, item.feedlabel].filter(Boolean);

                        return (
                          <a
                            key={item.id || item.url || item.title}
                            href={item.url || game.link || "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="ga-patch-item"
                            onClick={(event) => {
                              if (!item.url && !game.link) event.preventDefault();
                            }}
                          >
                            <div className="ga-patch-item__title">{item.title || "Official update"}</div>
                            {metaParts.length ? (
                              <div className="ga-patch-item__meta">{metaParts.join(" • ")}</div>
                            ) : null}
                          </a>
                        );
                      })
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <div className="ga-patch-footnote">
        Some publisher websites block automatic previews. The Open official button always keeps the
        original source available.
      </div>

      <style>{`
        .ga-patch-page{
          width: min(1480px, calc(100% - 32px));
          margin: 0 auto;
          padding: calc(var(--ga-header-clear, 170px) + 22px) 0 38px;
          color: var(--ga-text, rgba(255,255,255,0.94));
          font-family: Orbitron, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
        }

        .ga-patch-hero{
          display:grid;
          grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.75fr);
          align-items:end;
          gap:26px;
          padding: 24px;
          margin-bottom: 14px;
          border-radius: 24px;
          border: 1px solid rgba(255,255,255,0.14);
          background:
            radial-gradient(circle at 18% 20%, rgba(225,6,19,0.20), transparent 46%),
            linear-gradient(145deg, rgba(21,21,25,0.88), rgba(4,4,7,0.72));
          box-shadow: 0 22px 54px rgba(0,0,0,0.46);
          backdrop-filter: blur(8px);
        }

        .ga-patch-eyebrow{
          margin-bottom:8px;
          color: rgba(255,104,112,0.95);
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 1.8px;
          text-transform: uppercase;
        }

        .ga-patch-title{
          margin:0;
          font-size: clamp(34px, 4vw, 58px);
          line-height:1;
          letter-spacing:0.5px;
          text-transform:uppercase;
          text-shadow: 0 3px 24px rgba(0,0,0,0.68);
        }

        .ga-patch-subtitle{
          max-width: 830px;
          margin: 12px 0 0;
          color: rgba(255,255,255,0.76);
          font-family: Georgia, "Times New Roman", serif;
          font-size: 16px;
          line-height: 1.55;
        }

        .ga-patch-stats{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          margin-top:16px;
        }

        .ga-patch-stats span{
          padding:7px 10px;
          border-radius:999px;
          border:1px solid rgba(255,255,255,0.13);
          background:rgba(0,0,0,0.28);
          color:rgba(255,255,255,0.76);
          font-size:10px;
          font-weight:800;
          letter-spacing:0.3px;
        }

        .ga-patch-search{
          display:block;
          width:100%;
        }

        .ga-patch-search__label{
          display:block;
          margin:0 0 8px 2px;
          color:rgba(255,255,255,0.68);
          font-size:10px;
          font-weight:900;
          letter-spacing:1.1px;
          text-transform:uppercase;
        }

        .ga-patch-input{
          box-sizing:border-box;
          width:100%;
          min-height:48px;
          border-radius:16px;
          padding:12px 14px;
          border:1px solid rgba(255,255,255,0.17);
          background:rgba(0,0,0,0.42);
          color:rgba(255,255,255,0.94);
          outline:none;
          box-shadow:0 10px 26px rgba(0,0,0,0.34);
          font:600 13px Orbitron, system-ui, sans-serif;
        }

        .ga-patch-input:focus{
          border-color:rgba(225,6,19,0.72);
          box-shadow:0 0 0 3px rgba(225,6,19,0.16), 0 10px 26px rgba(0,0,0,0.34);
        }

        .ga-patch-input::placeholder{ color:rgba(255,255,255,0.48); }

        .ga-patch-toolbar{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:14px;
          margin:0 0 14px;
          padding:12px 14px;
          border-radius:18px;
          border:1px solid rgba(255,255,255,0.11);
          background:rgba(5,5,8,0.64);
          backdrop-filter:blur(8px);
        }

        .ga-patch-filters{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
        }

        .ga-patch-filter{
          border:1px solid rgba(255,255,255,0.14);
          border-radius:999px;
          padding:8px 11px;
          background:rgba(255,255,255,0.045);
          color:rgba(255,255,255,0.76);
          cursor:pointer;
          font:800 10px Orbitron, system-ui, sans-serif;
          transition:transform 120ms ease, border-color 120ms ease, background 120ms ease;
        }

        .ga-patch-filter:hover{
          transform:translateY(-1px);
          border-color:rgba(225,6,19,0.42);
        }

        .ga-patch-filter.is-active{
          border-color:rgba(255,73,83,0.64);
          background:linear-gradient(180deg, rgba(185,10,20,0.68), rgba(94,0,7,0.72));
          color:#fff;
          box-shadow:0 8px 20px rgba(150,0,10,0.24);
        }

        .ga-patch-result-count{
          flex:0 0 auto;
          color:rgba(255,255,255,0.58);
          font-size:10px;
          font-weight:800;
        }

        .ga-patch-grid{
          display:grid;
          grid-template-columns:repeat(3, minmax(0, 1fr));
          gap:14px;
        }

        .ga-patch-card{
          min-width:0;
          border-radius:20px;
          border:1px solid rgba(255,255,255,0.12);
          background:linear-gradient(180deg, rgba(18,18,21,0.82), rgba(0,0,0,0.64));
          box-shadow:0 18px 40px rgba(0,0,0,0.42);
          overflow:hidden;
          backdrop-filter:blur(5px);
        }

        .ga-patch-card--open{
          border-color:rgba(225,6,19,0.30);
        }

        .ga-patch-card__top{
          padding:16px 16px 12px;
          display:flex;
          flex-direction:column;
          gap:14px;
        }

        .ga-patch-card__badges{
          display:flex;
          flex-wrap:wrap;
          gap:7px;
          margin-bottom:10px;
        }

        .ga-patch-category,
        .ga-patch-source{
          display:inline-flex;
          align-items:center;
          min-height:22px;
          padding:3px 8px;
          border-radius:999px;
          font-size:9px;
          font-weight:900;
          letter-spacing:0.4px;
          text-transform:uppercase;
        }

        .ga-patch-category{
          border:1px solid rgba(255,77,87,0.36);
          background:rgba(159,0,11,0.24);
          color:rgba(255,189,193,0.94);
        }

        .ga-patch-source{
          border:1px solid rgba(255,255,255,0.11);
          background:rgba(255,255,255,0.04);
          color:rgba(255,255,255,0.58);
        }

        .ga-patch-card__title{
          margin:0;
          font-size:18px;
          line-height:1.2;
          letter-spacing:0.25px;
        }

        .ga-patch-card__description{
          min-height:34px;
          margin:6px 0 0;
          color:rgba(255,255,255,0.62);
          font-family:Georgia, "Times New Roman", serif;
          font-size:13px;
          line-height:1.35;
        }

        .ga-patch-card__actions{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
        }

        .ga-patch-btn{
          border-radius:12px;
          padding:8px 10px;
          border:1px solid rgba(255,255,255,0.16);
          background:linear-gradient(180deg, rgba(177,5,15,0.76), rgba(91,0,7,0.84));
          color:rgba(255,255,255,0.94);
          cursor:pointer;
          text-decoration:none;
          font:800 10px Orbitron, system-ui, sans-serif;
          line-height:1.2;
          transition:transform 120ms ease, border-color 120ms ease, filter 120ms ease;
        }

        .ga-patch-btn:hover{
          transform:translateY(-1px);
          filter:brightness(1.08);
          border-color:rgba(255,87,96,0.48);
        }

        .ga-patch-btn:disabled{
          opacity:0.58;
          cursor:not-allowed;
          transform:none;
        }

        .ga-patch-btn--ghost{
          background:rgba(0,0,0,0.27);
        }

        .ga-patch-note{
          margin:0 14px 12px;
          border-radius:14px;
          padding:10px 12px;
          font-family:Georgia, "Times New Roman", serif;
          font-size:12px;
          line-height:1.4;
          border:1px solid rgba(255,255,255,0.12);
          background:rgba(0,0,0,0.30);
          color:rgba(255,255,255,0.82);
        }

        .ga-patch-note--warn{
          border-color:rgba(255,58,74,0.28);
          background:rgba(145,0,13,0.16);
        }

        .ga-patch-card__hint{
          padding:0 16px 16px;
          color:rgba(255,255,255,0.56);
          font-family:Georgia, "Times New Roman", serif;
          font-size:12px;
          line-height:1.4;
        }

        .ga-patch-list{
          padding:11px 11px 13px;
          border-top:1px solid rgba(255,255,255,0.10);
          display:flex;
          flex-direction:column;
          gap:8px;
          max-height:520px;
          overflow-y:auto;
        }

        .ga-patch-item{
          display:block;
          border-radius:14px;
          padding:11px 12px;
          text-decoration:none;
          border:1px solid rgba(255,255,255,0.10);
          background:rgba(0,0,0,0.28);
          color:rgba(255,255,255,0.92);
          transition:transform 120ms ease, border-color 120ms ease, background 120ms ease;
        }

        .ga-patch-item:hover{
          transform:translateY(-1px);
          border-color:rgba(225,6,19,0.40);
          background:rgba(45,0,5,0.32);
        }

        .ga-patch-item__title{
          font-size:12px;
          font-weight:800;
          letter-spacing:0.15px;
          line-height:1.3;
        }

        .ga-patch-item__meta{
          margin-top:6px;
          color:rgba(255,255,255,0.56);
          font-size:9px;
          font-weight:700;
        }

        .ga-patch-empty,
        .ga-patch-state{
          padding:22px;
          border-radius:18px;
          border:1px solid rgba(255,255,255,0.11);
          background:rgba(0,0,0,0.42);
          color:rgba(255,255,255,0.68);
          font-family:Georgia, "Times New Roman", serif;
          text-align:center;
        }

        .ga-patch-footnote{
          margin-top:14px;
          padding:12px 14px;
          border-radius:16px;
          border:1px solid rgba(255,255,255,0.10);
          background:rgba(0,0,0,0.45);
          color:rgba(255,255,255,0.54);
          font-family:Georgia, "Times New Roman", serif;
          font-size:12px;
          line-height:1.45;
          text-align:center;
        }

        @media (max-width: 1180px){
          .ga-patch-grid{ grid-template-columns:repeat(2, minmax(0, 1fr)); }
        }

        @media (max-width: 900px){
          .ga-patch-hero{ grid-template-columns:1fr; align-items:stretch; }
          .ga-patch-toolbar{ align-items:flex-start; flex-direction:column; }
          .ga-patch-result-count{ padding-left:2px; }
        }

        @media (max-width: 720px){
          .ga-patch-page{
            width:min(calc(100% - 20px), 1480px);
            padding-top:calc(var(--ga-header-clear, 170px) + 14px);
          }
          .ga-patch-hero{ padding:18px; border-radius:20px; }
          .ga-patch-grid{ grid-template-columns:1fr; }
          .ga-patch-title{ font-size:34px; }
          .ga-patch-card__actions{ display:grid; grid-template-columns:1fr 1fr; }
          .ga-patch-btn{ text-align:center; }
          .ga-patch-card__actions .ga-patch-btn:first-child{ grid-column:1 / -1; }
        }
      `}</style>
    </div>
  );
}
